import React, { useEffect, useState } from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import ApiService from "../../services/ApiService";
import Loader from "../../components/Loader";
import { formatCurrency, formatDate, formatDateLocal } from '../../utils/helper';
import { toast } from 'react-toastify';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const PurchaseReport = () => {
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState([]);
    const [totalPurchases, setTotalPurchases] = useState(0);
    const [stats, setStats] = useState({
        this_month: 0,
        last_month: 0,
        percentage_change: 0
    });
    const [dateRange, setDateRange] = useState([null, null]);
    const [startDate, endDate] = dateRange;
    const [filters, setFilters] = useState({
        start_date: '',
        end_date: ''
    });

    const fetchReport = async () => {
        setLoading(true);
        try {
            const response = await ApiService.request({
                method: 'GET',
                url: 'admin/reports/purchases',
                data: filters
            });
            if (response.data.status) {
                setData(response.data.data.purchases);
                setTotalPurchases(response.data.data.total_purchases);
                if (response.data.data.stats) {
                    setStats(response.data.data.stats);
                }
            }
        } catch (error) {
            console.error(error);
            toast.error("Failed to fetch purchase report");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchReport();
    }, [filters]);

    const handleDateChange = (update) => {
        setDateRange(update);
        const [start, end] = update;
        setFilters(prev => ({
            ...prev,
            start_date: start ? formatDateLocal(start) : "",
            end_date: end ? formatDateLocal(end) : ""
        }));
    };

    const exportToExcel = () => {
        const csvContent = "data:text/csv;charset=utf-8," 
            + "Purchase ID,Date,Supplier,Items,Total Amount\n"
            + data.map(e => `${e.id},${formatDate(e.purchase_date)},${e.supplier_name || 'N/A'},${e.items_count || '-'},${e.total_amount}`).join("\n");
        const encodedUri = encodeURI(csvContent);
        const link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        link.setAttribute("download", `purchase_report_${filters.start_date}_to_${filters.end_date}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <div className="d-flex flex-column flex-column-fluid">
            <div id="kt_app_toolbar" className="app-toolbar py-3 py-lg-6">
                <div id="kt_app_toolbar_container" className="app-container container-fluid d-flex flex-stack">
                    <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3">
                        <h1 className="page-heading d-flex text-dark fw-bold fs-3 flex-column justify-content-center my-0">
                            Purchase Report
                        </h1>
                    </div>
                </div>
            </div>

            <div id="kt_app_content" className="app-content flex-column-fluid">
                <div id="kt_app_content_container" className="app-container container-fluid">
                    
                    {/* Stats Cards */}
                    <div className="row g-5 g-xl-8 mb-5 mb-xl-10">
                        <div className="col-xl-6">
                            <div className="card bg-body hoverable card-xl-stretch mb-xl-8">
                                <div className="card-body">
                                    <i className="fas fa-shopping-cart fs-2x text-primary mb-5"></i>
                                    <div className="text-gray-900 fw-bold fs-2 mb-2 mt-5">
                                        {formatCurrency(totalPurchases)}
                                    </div>
                                    <div className="fw-semibold text-gray-400">Total Purchases (Selected Period)</div>
                                </div>
                            </div>
                        </div>
                        <div className="col-xl-6">
                            <div className="card bg-body hoverable card-xl-stretch mb-xl-8">
                                <div className="card-body">
                                    <i className="fas fa-calendar-alt fs-2x text-success mb-5"></i>
                                    <div className="d-flex align-items-center mb-2 mt-5">
                                        <div className="text-gray-900 fw-bold fs-2 me-3">
                                            {formatCurrency(stats.this_month)}
                                        </div>
                                        <div className={`badge badge-light-${stats.percentage_change >= 0 ? 'success' : 'danger'} fs-base`}>
                                            <i className={`fas fa-arrow-${stats.percentage_change >= 0 ? 'up' : 'down'} fs-5 text-${stats.percentage_change >= 0 ? 'success' : 'danger'} ms-1`}></i>
                                            {Math.abs(stats.percentage_change)}%
                                        </div>
                                    </div>
                                    <div className="fw-semibold text-gray-400">
                                        This Month vs Last Month ({formatCurrency(stats.last_month)})
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="card mb-5 mb-xl-10">
                        <div className="card-header border-0 pt-6">
                            <div className="card-title">
                                <div className="d-flex align-items-center position-relative my-1">
                                    <DatePicker
                                        selectsRange={true}
                                        startDate={startDate}
                                        endDate={endDate}
                                        onChange={handleDateChange}
                                        className="form-control form-control-solid w-250px"
                                        placeholderText="Select Date Range"
                                        dateFormat="yyyy-MM-dd"
                                    />
                                </div>
                            </div>
                            <div className="card-toolbar">
                                <div className="d-flex justify-content-end gap-2">
                                    <OverlayTrigger placement="top" overlay={<Tooltip>Export to Excel</Tooltip>}>
                                        <button onClick={exportToExcel} className="btn btn-light-primary">
                                            <i className="fas fa-file-export me-2"></i> Export to Excel
                                        </button>
                                    </OverlayTrigger>
                                </div>
                            </div>
                        </div>

                        <div className="card-body py-4">
                            {loading ? <Loader /> : (
                                <div className="table-responsive">
                                    <table className="table align-middle table-row-dashed fs-6 gy-5">
                                        <thead>
                                            <tr className="text-start text-muted fw-bold fs-7 text-uppercase gs-0">
                                                <th>Purchase No</th>
                                                <th>Supplier</th>
                                                <th>Date</th>
                                                <th>Status</th>
                                                <th className="text-end">Amount</th>
                                            </tr>
                                        </thead>
                                        <tbody className="text-gray-600 fw-semibold">
                                            {data.length > 0 ? (
                                                data.map((purchase) => (
                                                    <tr key={purchase.id}>
                                                        <td>{purchase.purchase_number}</td>
                                                        <td>{purchase.supplier_name}</td>
                                                        <td>{formatDate(purchase.purchase_date)}</td>
                                                        <td>
                                                            <span className={`badge badge-light-${purchase.status === 'received' ? 'success' : 'warning'}`}>
                                                                {purchase.status}
                                                            </span>
                                                        </td>
                                                        <td className="text-end">{formatCurrency(purchase.total_amount)}</td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan="5" className="text-center">No records found</td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PurchaseReport;
